<template>
  <PageLoading v-if="loading" />
  <ErrorMessage v-else-if="error" />
  <V1ScreenScaffold
    v-else-if="paymentInfo && paymentInfo.installments.length > 0"
    :title="paymentInfo.title"
    :next-disabled="!canContinue"
    data-testid="v1-installments"
    @next="handleContinue"
  >
    <template #subtitle>
      <p
        v-if="paymentInfo.validThrough && paymentInfo.validThrough.display"
        class="v1-installments__pay-before"
        data-testid="v1-pay-before"
      >
        <span :class="paymentInfo.validThrough.isOverdue ? 'v1-installments__pay-before--overdue' : ''">
          {{ paymentInfo.validThrough.label }}
        </span>
        {{ paymentInfo.validThrough.display }}
      </p>
    </template>

    <RadioGroup
      :model-value="selectedValue"
      :options="installmentOptions"
      :aria-label="paymentInfo.title"
      data-testid="v1-installment-options"
      @update:model-value="selectedValue = String($event)"
    />

    <button
      v-if="hasTail"
      type="button"
      class="v1-installments__toggle"
      data-testid="v1-installments-toggle"
      @click="expanded = !expanded"
    >
      {{ expanded ? paymentInfo.visibility.labelExpanded : paymentInfo.visibility.labelCollapsed }}
    </button>

    <RadioGroup
      v-if="customOptions.length > 0"
      :model-value="selectedValue"
      :options="customOptions"
      data-testid="v1-other-amount"
      @update:model-value="selectedValue = String($event)"
    />

    <CustomerioBanner />
  </V1ScreenScaffold>
  <NoPayments v-else />
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { RadioGroup, RadioGroupOption } from "@payjoy/design-system";
import V1ScreenScaffold from "@/components/v1/V1ScreenScaffold.vue";
import PageLoading from "@/components/PageLoading.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import NoPayments from "@/components/NoPayments.vue";
import CustomerioBanner from "@/components/CustomerioBanner.vue";
import { fetchV1PaymentInfo } from "@/helpers/v1TestHelper";
import { IV1PaymentInfo } from "@/types/IV1PaymentInfo";

const CUSTOM_VALUE = "custom";

/**
 * v1 installments screen (PYMT-2285, restyled to the new UI in PYMT-2958).
 *
 * Consumes `GET /api/v1/payment-info/`: the response's `visibility.count` splits
 * the ordered `installments` into a default-visible set and a collapsible tail.
 * The amounts render as a `RadioGroup`; a selection (or the "other amount" option)
 * is confirmed with Continue, routing to the providers or custom-amount screen.
 */
export default defineComponent({
  name: "V1InstallmentsView",
  components: { V1ScreenScaffold, RadioGroup, PageLoading, ErrorMessage, NoPayments, CustomerioBanner },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const paymentInfo = ref<IV1PaymentInfo | null>(null);
    const loading = ref<boolean>(true);
    const error = ref<boolean>(false);
    const expanded = ref<boolean>(false);
    const selectedValue = ref<string | null>(null);

    const hasTail = computed<boolean>(() =>
      paymentInfo.value ? paymentInfo.value.installments.length > paymentInfo.value.visibility.count : false
    );

    const installmentOptions = computed<RadioGroupOption[]>(() => {
      const info = paymentInfo.value;
      if (!info) return [];
      const rows = expanded.value ? info.installments : info.installments.slice(0, info.visibility.count);
      return rows.map((installment, index) => ({
        value: `inst-${index}`,
        title: installment.amount.display,
        subtitle: installment.term,
      }));
    });

    const customOptions = computed<RadioGroupOption[]>(() => {
      const info = paymentInfo.value;
      if (!info?.allowsCustomAmount || !info.customAmountLabel) return [];
      return [{ value: CUSTOM_VALUE, title: info.customAmountLabel, subtitle: info.customAmountSubtitle }];
    });

    const canContinue = computed<boolean>(() => selectedValue.value !== null);

    onMounted(async () => {
      try {
        paymentInfo.value = await fetchV1PaymentInfo();
      } catch (fetchError) {
        console.error("[v1] Failed to fetch payment-info:", fetchError);
        error.value = true;
      } finally {
        loading.value = false;
      }
    });

    const handleContinue = (): void => {
      const value = selectedValue.value;
      if (value === null || !paymentInfo.value) return;
      if (value === CUSTOM_VALUE) {
        router.push({ name: "V1OtherAmountByDevice", params: { deviceTag: route.params.deviceTag } });
        return;
      }
      const installment = paymentInfo.value.installments[Number(value.replace("inst-", ""))];
      router.push({
        name: "V1ProvidersByDevice",
        params: { deviceTag: route.params.deviceTag },
        query: { amount: installment.amount.value, days: String(installment.days) },
      });
    };

    return {
      t,
      paymentInfo,
      loading,
      error,
      expanded,
      selectedValue,
      hasTail,
      installmentOptions,
      customOptions,
      canContinue,
      handleContinue,
    };
  },
});
</script>

<style scoped>
.v1-installments__pay-before {
  margin: 0 0 24px 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--pj-color-brand-gray-850, #222926);
}

.v1-installments__toggle {
  align-self: flex-end;
  width: 100%;
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  color: var(--pj-color-brand-green-500, #007c3b);
  cursor: pointer;
}
</style>
